import { useCreateFunctionMutation } from 'store/services/helios/functionsService';
import { useCallback, useMemo, useState } from 'react';
import type { InferType } from 'yup';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, FormProvider, useWatch, useFieldArray } from 'react-hook-form';
import { Box, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { yupResolver } from '@hookform/resolvers/yup';
import { MenuItem } from '@mui/material';
import { toast } from 'react-toastify';

import type { CreateFunctionReq, FunctionType, FunctionWorkflowCommentType } from 'types/functions';
import type { ModalsType } from 'types';
import { TextField } from 'components/mui/common/TextField';
import { Switch } from 'components/mui/common/Switch';
import { ActionModal } from 'components/mui/common/ActionModal';
import { useAddFunctionModalSchema } from 'utils/validations/helios/useHeliosFunctionsSchema';
import { Select } from 'components/mui/common/Select';
import { LookupField } from 'components/mui/common/LookupField';
import { SelectDatasourceModal } from 'components/modals/SelectHeliosDataSourceModal';
import type { AddParametersModalSchema } from './AddParametersToFunctionModal';
import { AddParametersToFunctionModal } from './AddParametersToFunctionModal';

import { Button } from 'components/mui/common/Button';
import { useModals } from 'utils/hooks';
import { translateValues, TranslationField } from 'components/mui/common/TranslationField';
import { BooleanWrapper, ChipsWrapper, FormWrapper, SectionTitle, StyledChip } from './FunctionModals.styles';

export interface FunctionParameter {
  name: string;
  labelText: string;
  functionParameterType: string;
  dateFormatVariant?: string | null;
  order: number;
  isRequired: boolean;
  functionParameterSelectedList: Array<{
    name: string;
    value: string;
  }>;
  templateBrowseId?: string;
}

const defaultValues = {
  name: '',
  column: '',
  functionType: '',
  heliosFunctionId: 1,
  isCommon: true,
  isEntryFunction: false,
  parentColumn: '',
  datasourceId: '',
  datasourceName: '',
  translatesName: translateValues,
  functionParameters: [],
  functionWorkflowCommentType: 'None',
};

export const ADD_FUNCTION_MODAL_TRANSLATE_PATH = 'helios.functions.addFunctionModal';

export type AddFunctionModalValidationSchema = InferType<ReturnType<typeof useAddFunctionModalSchema>>;

export const AddFunctionModal: React.FC<ModalsType> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [isDatasourcePickerOpen, setIsDatasourcePickerOpen] = useState(false);
  const [modals, openModal, closeModal] = useModals({
    addParametersModal: false,
    translationModal: false,
  });

  const [editingParameterIndex, setEditingParameterIndex] = useState<number | null>(null);
  const [createFunction] = useCreateFunctionMutation();

  const addFunctionModalSchema = useAddFunctionModalSchema();

  const methods = useForm<AddFunctionModalValidationSchema>({
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(addFunctionModalSchema),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { isSubmitting },
  } = methods;
  const [entryFunction, functionType, datasourceId, datasourceName] = useWatch({
    control,
    name: ['entryFunction', 'functionType', 'datasourceId', 'datasourceName'],
  });

  const functionTypes = useMemo(
    () => [
      { value: 'Heg', name: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionTypes.Heg`) },
      { value: 'HegWorkflow', name: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionTypes.HegWorkflow`) },
    ],
    [t]
  );

  const functionWorkflowCommentTypes = useMemo(
    () => [
      { value: 'None', name: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.worflowCommentType.none`) },
      { value: 'Optional', name: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.worflowCommentType.optional`) },
      { value: 'Required', name: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.worflowCommentType.required`) },
    ],
    [t]
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'functionParameters',
  });

  const handleAddParameter = useCallback(
    (parameter: AddParametersModalSchema) => {
      if (editingParameterIndex !== null) {
        setValue(`functionParameters.${editingParameterIndex}`, parameter);
        setEditingParameterIndex(null);
      } else append(parameter);

      closeModal('addParametersModal')();
    },
    [append, closeModal, editingParameterIndex, setValue]
  );

  const handleEditParameter = useCallback(
    (index: number) => {
      setEditingParameterIndex(index);
      const parameterToEdit = getValues(`functionParameters.${index}`) ?? null;
      setValue('currentParameter', parameterToEdit);
      openModal('addParametersModal')();
    },
    [getValues, openModal, setValue]
  );

  const handleClose = useCallback(() => {
    reset();
    setEditingParameterIndex(null);
    setIsDatasourcePickerOpen(false);
    onClose();
  }, [reset, onClose]);

  const onSubmit = async (values: AddFunctionModalValidationSchema) => {
    const data: CreateFunctionReq = {
      name: values.name,
      column: values.column ?? '',
      functionType: values.functionType as FunctionType,
      heliosFunctionId: values.heliosFunctionId,
      datasourceId: values.datasourceId,
      parentColumn: values.parentColumn ?? '',
      translatesName: (values.translatesName ?? []) as CreateFunctionReq['translatesName'],
      isCommon: Boolean(values.isCommon),
      entryFunction: Boolean(values.entryFunction),
      functionParameters: (values.functionParameters ?? []) as CreateFunctionReq['functionParameters'],
      functionWorkflowCommentType: (values.functionWorkflowCommentType as FunctionWorkflowCommentType) || 'None',
      skipRecordsOnError: Boolean(values.skipRecordsOnError),
    };

    try {
      const res = await createFunction({ data });
      if (!('error' in res)) {
        toast.success(t('helios.functions.toasts.success.add'));
        handleClose();
      } else {
        toast.error(t('helios.functions.toasts.errors.add'));
      }
    } catch (_error) {
      toast.error(t('helios.functions.toasts.errors.add'));
    }
  };

  return (
    <ActionModal
      open={open}
      title={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.title`)}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      disableBackdropClick={isSubmitting}
      cancelText={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.buttons.cancel`)}
      submitText={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.buttons.add`)}
      submitIcon={{
        icon: <AddIcon color="inherit" />,
        position: 'start',
      }}>
      <FormProvider {...methods}>
        <FormWrapper>
          <SectionTitle variant="caption">{t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.sections.basicInfo`)}</SectionTitle>

          <Box sx={{ gridColumn: '1 / -1' }}>
            <Controller
              name="datasourceId"
              control={control}
              render={({ fieldState: { error } }) => (
                <LookupField
                  label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.dataSource`)}
                  value={datasourceName}
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                  data-testid="admin-helios-add-function-data-source-field"
                  onClick={() => setIsDatasourcePickerOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setIsDatasourcePickerOpen(true);
                    }
                  }}
                />
              )}
            />
          </Box>

          {isDatasourcePickerOpen && (
            <SelectDatasourceModal
              open
              onClose={() => setIsDatasourcePickerOpen(false)}
              title={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.dataSource`)}
              defaultSelectedDatasource={datasourceId}
              onConfirm={(selectedDatasource, selectedName) => {
                setValue('datasourceId', selectedDatasource, { shouldDirty: true, shouldValidate: true });
                setValue('datasourceName', selectedName ?? '');
                setIsDatasourcePickerOpen(false);
              }}
            />
          )}

          <Box sx={{ gridColumn: '1 / -1' }}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <TranslationField
                  label={t('common.name')}
                  {...field}
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                  background="white"
                  translatesName="translatesName"
                />
              )}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Controller
                  name="column"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      fullWidth
                      label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.column`)}
                    />
                  )}
                />
              </Grid>
              {entryFunction && (
                <Grid size={6}>
                  <Controller
                    name="parentColumn"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        fullWidth
                        label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.parentColumn`)}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Controller
            name="functionType"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                fullWidth
                label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionType`)}>
                {functionTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <Controller
            name="heliosFunctionId"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="number"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                fullWidth
                label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.id`)}
              />
            )}
          />

          <SectionTitle variant="caption">{t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.sections.parameters`)}</SectionTitle>

          <Box sx={{ gridColumn: '1 / -1' }}>
            <Button
              startIcon={<AddIcon />}
              color="secondary"
              variant="contained"
              onClick={openModal('addParametersModal')}
              sx={{ width: 'fit-content' }}>
              {t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.buttons.addParameters`)}
            </Button>
          </Box>

          {fields.length > 0 && (
            <ChipsWrapper>
              {fields.map((parameter, index) => (
                <StyledChip key={index} label={parameter.name} onDelete={() => remove(index)} onClick={() => handleEditParameter(index)} />
              ))}
            </ChipsWrapper>
          )}

          <SectionTitle variant="caption">{t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.sections.settings`)}</SectionTitle>

          <BooleanWrapper>
            <Controller
              name="isCommon"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(_e, checked) => field.onChange(checked)}
                  slotProps={{ input: { ref: field.ref } }}
                  name={field.name}
                  label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.isCommon`)}
                />
              )}
            />
            <Controller
              name="skipRecordsOnError"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(_e, checked) => field.onChange(checked)}
                  slotProps={{ input: { ref: field.ref } }}
                  name={field.name}
                  label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.skipEntriesWithError`)}
                />
              )}
            />
            <Controller
              name="entryFunction"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onChange={(_e, checked) => field.onChange(checked)}
                  slotProps={{ input: { ref: field.ref } }}
                  name={field.name}
                  label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.isEntryFunction`)}
                />
              )}
            />
          </BooleanWrapper>

          <Box sx={{ gridColumn: '1 / -1' }}>
            <Grid container spacing={2}>
              {functionType === 'HegWorkflow' && (
                <Grid size={6}>
                  <Controller
                    name="functionWorkflowCommentType"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Select
                        {...field}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        fullWidth
                        label={t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionWorkflowCommentType`)}>
                        {functionWorkflowCommentTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </FormWrapper>
        <AddParametersToFunctionModal
          open={modals.addParametersModal}
          datasourceId={datasourceId || undefined}
          onSubmit={handleAddParameter}
          onClose={() => {
            closeModal('addParametersModal')();
            setEditingParameterIndex(null);
          }}
          editingParameter={
            editingParameterIndex !== null
              ? (getValues(`functionParameters.${editingParameterIndex}`) as unknown as AddParametersModalSchema)
              : undefined
          }
        />
      </FormProvider>
    </ActionModal>
  );
};
